import { useEffect, useState } from 'react';

import { Collection, MeetingRoomsResponse, UseMeetingRoomsReturn } from '../types/meetingTypes';
import { apiStrapi, setupStrapiHeaders } from 'services/apiService';

// Cache em memória
let meetingRoomsCache: {
  data: Collection[] | null;
  timestamp: number;
  filter?: string;
  error?: string | null;
  status?: 'success' | 'not_found' | 'error';
  lastErrorTimestamp?: number;
} = {
  data: null,
  timestamp: 0,
  filter: undefined,
  error: null,
  status: 'success',
  lastErrorTimestamp: 0,
};

// Tempo de expiração do cache (5 minutos)
const CACHE_EXPIRATION = 5 * 60 * 1000;
// Tempo de expiração para erros (1 minuto)
const ERROR_CACHE_EXPIRATION = 1 * 60 * 1000;

// Função para transformar a sala no formato esperado pelo JitsiVideoEmbed
const transformRoom = (room: any) => ({
  ...room,
  room_data: {
    id: room.id,
    title: room.title,
    vendor_code: room.vendor_code,
    vendor_room_id: room.key,
    organization_id: '',
    is_open: true,
    description: '',
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    room_pwd: '',
    key: room.key,
    position: 0,
  },
  participation_status: {
    is_participant: true,
    role: 'participant',
  },
});

// Função para validar as collections
const validateCollections = (collections: Collection[]): { isValid: boolean; error?: string } => {
  if (!collections || !Array.isArray(collections)) {
    return { isValid: false, error: 'Formato inválido das salas' };
  }

  // Verifica se todas as collections têm as propriedades necessárias
  for (const collection of collections) {
    if (!collection.feature || !collection.rooms) {
      return { isValid: false, error: 'Propriedades obrigatórias não encontradas nas salas' };
    }
  }

  return { isValid: true };
};

// Função para obter mensagem de erro amigável
const getErrorMessage = (error: any): { message: string; status: 'not_found' | 'error' } => {
  if (error?.response?.status === 404) {
    return {
      message: 'Nenhuma sala de reunião cadastrada para seu usuário.',
      status: 'not_found',
    };
  }

  if (error?.response?.status === 403) {
    return {
      message: 'Você não tem permissão para acessar as salas de reunião.',
      status: 'error',
    };
  }

  return {
    message:
      'Não foi possível carregar as salas de reunião. Por favor, tente novamente mais tarde.',
    status: 'error',
  };
};

export const useMeetingRooms = (filter?: string): UseMeetingRoomsReturn => {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [status, setStatus] = useState<'success' | 'not_found' | 'error'>('success');

  useEffect(() => {
    const fetchMeetingRooms = async () => {
      try {
        const now = Date.now();

        // Proteção extra: garantir headers do Strapi
        const instance = apiStrapi.getInstance();
        const headers = instance.defaults.headers.common;
        const hasToken = !!headers['Authorization'];
        const hasUserPool = !!headers['X-Cognito-UserPool'];
        const hasOrgUUID = !!headers['X-Organization-UUID'];
        if (!hasToken || !hasUserPool || !hasOrgUUID) {
          // Tenta setar novamente
          const token = localStorage.getItem('AUTH_TOKEN') || undefined;
          const orgUUID = localStorage.getItem('x-organization-uuid') || undefined;
          const userPool = localStorage.getItem('x-cognito-userpool') || undefined;
          setupStrapiHeaders(token, orgUUID, userPool);
        }

        // Verifica se temos um erro recente no cache
        if (
          meetingRoomsCache.error &&
          meetingRoomsCache.lastErrorTimestamp &&
          now - meetingRoomsCache.lastErrorTimestamp < ERROR_CACHE_EXPIRATION
        ) {
          setError(meetingRoomsCache.error);
          setIsValid(false);
          setStatus(meetingRoomsCache.status || 'error');
          setCollections([]);
          setLoading(false);
          return;
        }

        // Verifica se temos dados em cache válidos e com o mesmo filtro
        if (
          meetingRoomsCache.data &&
          now - meetingRoomsCache.timestamp < CACHE_EXPIRATION &&
          meetingRoomsCache.filter === filter &&
          !meetingRoomsCache.error
        ) {
          setCollections(meetingRoomsCache.data);
          setError(null);
          setIsValid(true);
          setStatus(meetingRoomsCache.status || 'success');
          setLoading(false);
          return;
        }

        setLoading(true);

        const response = await apiStrapi.get<MeetingRoomsResponse>('/organization/meeting-rooms');

        // Valida se a resposta tem o formato esperado
        if (!response.data || !response.data.collections) {
          throw new Error('Formato inválido da resposta da API');
        }

        // Transforma os dados para incluir os campos necessários
        let transformedCollections = response.data.collections.map((collection: Collection) => ({
          ...collection,
          rooms: collection.rooms.map(transformRoom),
        }));

        // Aplica o filtro se fornecido
        if (filter) {
          transformedCollections = transformedCollections.filter(
            (collection: Collection) => collection.feature.name === filter
          );
        }

        // Valida as collections transformadas
        const validation = validateCollections(transformedCollections);
        if (!validation.isValid) {
          throw new Error(validation.error || 'Dados inválidos das salas');
        }

        // Atualiza o cache
        meetingRoomsCache = {
          data: transformedCollections,
          timestamp: now,
          filter,
          error: null,
          status: 'success',
        };

        setCollections(transformedCollections);
        setError(null);
        setIsValid(true);
        setStatus('success');
      } catch (err) {
        const { message, status: errorStatus } = getErrorMessage(err);
        setError(message);
        setIsValid(false);
        setStatus(errorStatus);

        // Atualiza o cache com o erro
        meetingRoomsCache = {
          ...meetingRoomsCache,
          error: message,
          status: errorStatus,
          lastErrorTimestamp: Date.now(),
        };
      } finally {
        setLoading(false);
      }
    };

    fetchMeetingRooms();
  }, [filter]);

  return { collections, loading, error, isValid, status };
};

// Função para limpar o cache manualmente (útil quando precisamos forçar uma atualização)
export const clearMeetingRoomsCache = () => {
  meetingRoomsCache = {
    data: null,
    timestamp: 0,
    filter: undefined,
    error: null,
    status: 'success',
    lastErrorTimestamp: 0,
  };
};
